import React, { useCallback } from "react";
import clsx from "clsx";

import type {
    GridProps,
    GetRowIdFunc,
    GridReadyEvent,
    IsFullWidthRowParams,
} from "@/ui/molecules/Grid";
import Grid, { GridLoaderSkeleton } from "@/ui/molecules/Grid";
import { AddAccountCellRenderer } from "./addAccountCellRenderer";

import Box from "@/ui/atoms/Box";
import type { TAccountDTO } from "@/store/accounts/accountTypes";
import { gridEditChartOfAccountsColumnDefs } from "./gridEditChartOfAccountsColumnDefs";
import type { TResponseGetCoaDefinitionByCoaId } from "@/apis/odin/apiODIN";
import { isNullable } from "@/utils/guards";

export type TAddAccountRow = {
    type: "add-account";
    code: string;
    displayOrder: number;
    id: number;
};

export type TGridChartOfAccountsRow = TAccountDTO | TAddAccountRow;

const isAddAccountRowTypeGuard = (
    row: TGridChartOfAccountsRow,
): row is TAddAccountRow => {
    return row.type === "add-account";
};

export type GridEditChartOfAccountsProps = Omit<
    GridProps<TGridChartOfAccountsRow>,
    "columnDefs" | "rowData" | "getRowStyle"
> & {
    updateAccount: (account: TAccountDTO) => void;
    deleteAccount: (accountId: number) => void;
    restoreAccount: (accountId: number) => void;
    onGridReady?: (params: GridReadyEvent) => void;
    onAddAccount: (account: TAccountDTO) => void;
    onEditAccounts: (considerNewAccounts: boolean) => void;
    title?: string;
    rowData: TAccountDTO[];
    coaDefinition: TResponseGetCoaDefinitionByCoaId;
    isEditable: boolean;
    showValidationErrors: boolean;
    errors: Record<number, Record<string, string>>;
    accountsToBeDeleted: number[];
};

export const testIds = {
    main: "GridChartOfAccounts",
    header: "GridChartOfAccounts-header",
    badge: "GridChartOfAccounts-badge",
    titleBar: "GridChartOfAccounts-titleBar",
};

const GridEditChartOfAccounts = React.memo(
    (props: GridEditChartOfAccountsProps) => {
        const {
            onGridReady,
            onAddAccount,
            deleteAccount,
            restoreAccount,
            updateAccount,
            onEditAccounts,
            rowData,
            accountsToBeDeleted,
            errors,
            className,
            isEditable,
            showValidationErrors,
            coaDefinition,
            ...otherProps
        } = props;

        const AddAccountRow = {
            type: "add-account",
            code: "acar",
            displayOrder: Number.MAX_SAFE_INTEGER,
            id: 0,
        } satisfies TAddAccountRow;

        const getRowId: GetRowIdFunc<TGridChartOfAccountsRow> =
            React.useCallback(params => String(params.data.id), []);

        const isFullWidthRow = useCallback(
            (params: IsFullWidthRowParams<TGridChartOfAccountsRow>) => {
                return Boolean(
                    params.rowNode?.data &&
                        isAddAccountRowTypeGuard(params.rowNode?.data),
                );
            },
            [],
        );

        const fullWidthCellRenderer = useCallback(AddAccountCellRenderer, []);

        const optionsAccountsArray = React.useMemo(
            () => [
                { value: "income", label: "Income" },
                { value: "expense", label: "Expense" },
                ...(coaDefinition.hasNonOperatingAccounts === true
                    ? [{ value: "non-operating", label: "Non-Operating" }]
                    : []),
            ],
            [coaDefinition],
        );

        const columnDefs = gridEditChartOfAccountsColumnDefs(
            deleteAccount,
            restoreAccount,
            showValidationErrors,
            isEditable,
            errors,
            coaDefinition,
            optionsAccountsArray,
            accountsToBeDeleted,
        );

        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    width: "100%",
                }}
            >
                <Grid<TGridChartOfAccountsRow>
                    data-testid={testIds.main}
                    className={clsx([testIds.main, className])}
                    gridTheme="ag-theme-custom-proda"
                    rowData={isEditable ? [...rowData, AddAccountRow] : rowData}
                    columnDefs={columnDefs}
                    getRowId={getRowId}
                    defaultColDef={{
                        suppressMovable: true,
                        sortable: false,
                        resizable: true,
                        flex: 1,
                    }}
                    getRowStyle={() => ({
                        backgroundColor: "var(--proda-white)",
                    })}
                    suppressHeaderFocus={true}
                    enableBrowserTooltips={true}
                    onGridReady={onGridReady}
                    headerHeight={48}
                    groupHeaderHeight={48}
                    gridLoaderComponent={GridLoaderSkeleton}
                    suppressRowVirtualisation={true}
                    suppressColumnVirtualisation={true}
                    isFullWidthRow={isFullWidthRow}
                    fullWidthCellRenderer={fullWidthCellRenderer}
                    fullWidthCellRendererParams={{
                        isEditable,
                        handleAddAccountButtonClicked: onAddAccount,
                    }}
                    onCellEditingStopped={params => {
                        if (
                            !isNullable(params.data) &&
                            !isNullable(updateAccount) &&
                            params.valueChanged === true
                        ) {
                            updateAccount(params.data as TAccountDTO);
                        }
                        onEditAccounts(false);
                    }}
                    {...otherProps}
                />
            </Box>
        );
    },
);

GridEditChartOfAccounts.displayName = "GridEditChartOfAccounts";

export default GridEditChartOfAccounts;
